import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Check, ChevronDown } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"

// Full Estimate — shared visual atoms.
//
// Design notes:
//   • "AI" never appears as a literal label on screen. The AI's
//     output IS the default content — every row that renders is
//     the AI's recommendation. We surface AI presence only via
//     subtle moves: an "Edited" badge when the user overrides,
//     a confidence flag when something needs review, and a
//     "Suggested: X" microcopy inside the edit form so the user
//     can revert to the AI pick if they want.
//   • Color language matches the rest of the app:
//       emerald = chosen / good
//       indigo  = "edited by user" / AI accent in micro doses
//       amber   = needs review (medium confidence)
//       rose    = critical (low confidence)
//   • All interactives ≥36px tap target; toggles ≥44px combined.

/* ── Segmented toggle ─────────────────────────────────────────── */

interface SegmentedToggleProps<T extends string> {
  options: ReadonlyArray<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  /** Disabled keeps the visual but kills click handling — used
   *  while a save is in flight so the user doesn't double-click. */
  disabled?: boolean
}

export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled,
}: SegmentedToggleProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cx(
        "relative inline-flex w-full p-1 rounded-xl bg-zinc-100",
        "ring-1 ring-zinc-200/70",
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={cx(
              "relative flex-1 h-10 rounded-lg",
              "text-[13px] font-semibold tracking-tight",
              "transition-colors duration-150",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              selected
                ? "text-emerald-900"
                : "text-zinc-500 hover:text-zinc-700",
            )}
          >
            {selected && (
              <motion.span
                layoutId={`seg-${ariaLabel}`}
                className="absolute inset-0 rounded-lg bg-white shadow-sm ring-1 ring-emerald-500/40"
                transition={{ type: "spring", stiffness: 480, damping: 38 }}
              />
            )}
            <span className="relative">{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}

/* ── Suggestion microcopy ──────────────────────────────────────
   Quiet hint inside an edit form. Only renders if the current value
   differs from the suggestion — gives the user a one-tap revert
   without permanently advertising "the AI thinks X". */

interface SuggestionMicrocopyProps<T extends string> {
  current: T
  suggested: T
  optionLabel: (value: T) => string
  onRevert: () => void
}

export function SuggestionMicrocopy<T extends string>({
  current,
  suggested,
  optionLabel,
  onRevert,
}: SuggestionMicrocopyProps<T>) {
  if (current === suggested) return null
  return (
    <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] leading-tight">
      <span className="text-zinc-400">Suggested:</span>
      <span className="text-zinc-600 font-medium">{optionLabel(suggested)}</span>
      <button
        type="button"
        onClick={onRevert}
        className="text-indigo-700 hover:text-indigo-900 font-semibold underline underline-offset-2 decoration-indigo-300/70"
      >
        Revert
      </button>
    </div>
  )
}

/* ── Edited badge ─────────────────────────────────────────────
   Appears on a row when the user overrode the AI's pick. Indigo
   so it reads as "you touched this" without going all-caps shouty. */

export function EditedBadge() {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 px-1.5 h-5 rounded-md",
        "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200",
        "text-[10px] font-bold uppercase tracking-wider",
      )}
    >
      Edited
    </span>
  )
}

/* ── Confidence flag ──────────────────────────────────────────
   Renders only for non-high confidence. High = no UI noise (the
   row is fine, move along). Medium = small amber dot + microcopy.
   Low = rose dot + microcopy. */

export function ConfidenceFlag({
  level,
}: {
  level: string | null | undefined
}) {
  const norm = (level ?? "").toLowerCase()
  if (norm === "high" || norm === "" ) return null

  const isLow = norm === "low"
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider",
        isLow ? "text-rose-700" : "text-amber-700",
      )}
    >
      <span
        className={cx(
          "size-1.5 rounded-full",
          isLow ? "bg-rose-500" : "bg-amber-500",
        )}
        aria-hidden
      />
      {isLow ? "Low confidence" : "Review"}
    </span>
  )
}

/* ── Severity bar ─────────────────────────────────────────────
   1–10. Gradient fill from emerald (1–3) → amber (4–6) → rose
   (7–10). Pip floats above the fill at the value position. */

export function SeverityBar({
  value,
  onChange,
}: {
  value: number
  onChange?: (v: number) => void
}) {
  const clamped = Math.max(1, Math.min(10, value))
  const pct = ((clamped - 1) / 9) * 100

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
          Severity
        </span>
        <span className="text-[12px] font-bold text-zinc-800 tabular-nums">
          {clamped}
          <span className="text-zinc-400">/10</span>
        </span>
      </div>
      <div className="relative h-2 rounded-full overflow-hidden bg-zinc-100">
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${pct}%`,
            background:
              "linear-gradient(90deg, #10b981 0%, #fbbf24 55%, #f43f5e 100%)",
          }}
        />
      </div>
      {onChange && (
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={clamped}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label="Severity"
          className="w-full h-2 -mt-2 opacity-0 cursor-pointer"
        />
      )}
    </div>
  )
}

/* ── Section count chip ───────────────────────────────────────
   Tiny pill used in section headers: "2 components" or "3
   operations". Stays neutral; doesn't compete with the section
   title. */

export function CountChip({
  count,
  noun,
}: {
  count: number
  noun: string
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 h-6 rounded-full",
        "bg-zinc-100 text-zinc-600",
        "text-[11px] font-semibold tabular-nums",
      )}
    >
      {count} {count === 1 ? noun : `${noun}s`}
    </span>
  )
}

/* ── Section header ───────────────────────────────────────────
   Shared header strip used by both the damage and operations
   step. Eyebrow shows the section name; right side has count
   chip + optional remove button. */

export function SectionHeader({
  title,
  count,
  noun,
  onRemove,
  removeAriaLabel,
}: {
  title: string
  count: number
  noun: string
  onRemove?: () => void
  removeAriaLabel?: string
}) {
  return (
    <div className="px-4 md:px-5 py-3 bg-zinc-50/80 border-b border-zinc-100 flex items-center gap-3">
      <h3 className="text-[12.5px] font-bold uppercase tracking-[0.14em] text-zinc-700 truncate flex-1">
        {title}
      </h3>
      <CountChip count={count} noun={noun} />
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeAriaLabel ?? "Remove section"}
          className="size-7 rounded-lg text-zinc-400 hover:text-rose-700 hover:bg-rose-50 grid place-items-center transition-colors duration-150"
        >
          <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden fill="none">
            <path
              d="M3 4h10M6.5 7v4M9.5 7v4M5 4l.5-1.5h5L11 4M4 4v9a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  )
}

/* ── Dropdown ─────────────────────────────────────────────────
   Custom select control matching the design system. Replaces
   native <select> for in-form pickers (component, operation
   type) so the trigger looks like our other inputs and the
   popover follows our rounded-xl/shadow-xl pattern.

   The popover uses position:fixed with computed coordinates
   from the trigger — this lets it escape any parent
   `overflow:hidden` (e.g. the edit accordion) without needing
   a React portal. Updates on scroll/resize. */

interface DropdownOption<T extends string> {
  value: T
  label: string
}

interface DropdownProps<T extends string> {
  value: T
  options: ReadonlyArray<DropdownOption<T>>
  onChange: (value: T) => void
  ariaLabel: string
  placeholder?: string
  disabled?: boolean
  /** Optional fallback label rendered when the current value isn't
   *  in the options list (e.g. an out-of-curated-list BE value). */
  currentLabelFallback?: string
}

export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder,
  disabled,
  currentLabelFallback,
}: DropdownProps<T>) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{
    top: number
    left: number
    width: number
    placeAbove: boolean
  } | null>(null)

  // Compute popover position whenever opened or the page moves.
  // We open below by default; if there isn't room, we flip above.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const popMaxHeight = 280
      const spaceBelow = window.innerHeight - rect.bottom
      const placeAbove = spaceBelow < popMaxHeight && rect.top > popMaxHeight
      setPos({
        top: placeAbove ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        placeAbove,
      })
    }
    update()
    // Capture-phase scroll catches scrolls on any ancestor (the
    // wizard container, the page), keeping the popover glued to
    // the trigger.
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [open])

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node
      if (
        triggerRef.current &&
        !triggerRef.current.contains(t) &&
        popoverRef.current &&
        !popoverRef.current.contains(t)
      ) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onClick)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onClick)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const selected = options.find((o) => o.value === value)
  const triggerLabel =
    selected?.label ?? currentLabelFallback ?? placeholder ?? "Select…"

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((s) => !s)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={cx(
          "w-full h-11 rounded-lg bg-white border",
          "px-3 text-[13.5px] font-medium text-zinc-800",
          "flex items-center justify-between gap-2 text-left",
          "outline-none transition-colors duration-150",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          open
            ? "border-emerald-400 ring-2 ring-emerald-500/20"
            : "border-zinc-200 hover:border-zinc-300",
          "focus-visible:border-emerald-400 focus-visible:ring-2 focus-visible:ring-emerald-500/20",
        )}
      >
        <span
          className={cx(
            "truncate flex-1",
            !selected && "text-zinc-400 font-normal",
          )}
        >
          {triggerLabel}
        </span>
        <ChevronDown
          className={cx(
            "size-4 text-zinc-400 shrink-0 transition-transform duration-150",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      <AnimatePresence>
        {open && pos && (
          <motion.div
            ref={popoverRef}
            initial={{
              opacity: 0,
              y: pos.placeAbove ? 4 : -4,
            }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: pos.placeAbove ? 4 : -4,
            }}
            transition={{ duration: 0.12 }}
            role="listbox"
            aria-label={ariaLabel}
            style={{
              position: "fixed",
              top: pos.placeAbove ? undefined : pos.top,
              bottom: pos.placeAbove
                ? window.innerHeight - pos.top
                : undefined,
              left: pos.left,
              width: pos.width,
              maxHeight: 280,
              zIndex: 100,
            }}
            className={cx(
              "rounded-xl bg-white border border-zinc-200",
              "shadow-xl shadow-zinc-900/[0.10]",
              "overflow-y-auto overscroll-contain",
            )}
          >
            <ul className="py-1.5">
              {options.length === 0 ? (
                <li className="px-3 py-3 text-[12.5px] text-zinc-400">
                  No options for this section
                </li>
              ) : (
                options.map((opt) => {
                  const isSelected = opt.value === value
                  return (
                    <li key={opt.value} role="option" aria-selected={isSelected}>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(opt.value)
                          setOpen(false)
                        }}
                        className={cx(
                          "w-full text-left px-3 py-2.5 flex items-center gap-2",
                          "transition-colors duration-100",
                          "focus:outline-none focus-visible:bg-zinc-100",
                          isSelected
                            ? "bg-emerald-50 text-emerald-900"
                            : "text-zinc-700 hover:bg-zinc-50",
                        )}
                      >
                        <span
                          className={cx(
                            "truncate flex-1 text-[13px]",
                            isSelected && "font-semibold",
                          )}
                        >
                          {opt.label}
                        </span>
                        {isSelected && (
                          <Check
                            className="size-3.5 text-emerald-600 shrink-0"
                            strokeWidth={2.6}
                            aria-hidden
                          />
                        )}
                      </button>
                    </li>
                  )
                })
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

/* ── Add row button ───────────────────────────────────────────
   Used at the bottom of a section card ("+ Add component" /
   "+ Add operation"). Dashed border so it reads as an empty
   slot, not another item. */

export function AddRowButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "w-full h-11 rounded-lg",
        "border border-dashed border-zinc-300 hover:border-emerald-400",
        "bg-white hover:bg-emerald-50/40 transition-colors duration-150",
        "flex items-center justify-center gap-1.5",
        "text-[12.5px] font-semibold text-zinc-500 hover:text-emerald-800",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40",
      )}
    >
      <svg
        viewBox="0 0 16 16"
        className="size-3.5"
        aria-hidden
        fill="none"
        strokeWidth="2.2"
        stroke="currentColor"
        strokeLinecap="round"
      >
        <path d="M8 3v10M3 8h10" />
      </svg>
      {label}
    </button>
  )
}
